import { useEffect, useState, useCallback, useSyncExternalStore } from 'react';
import useStyles from './styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';

/**
 * Cookie disclaimer text displayed to users.
 */
const disclaimerText = `We use 🍪 cookies to secure your sessions and data while using our application(s). If you have questions about our cookie usage, please contact support at support@elevatormag.com.`;

/**
 * Date identifier for the disclaimer version.
 * Changing this date will reset the disclaimer for all users.
 */
const disclaimerDate = '02-17-2021';

/**
 * LocalStorage key used to track if the user has accepted the cookie disclaimer.
 * Format: `cookieDisclaimer_{disclaimerDate}`
 */
export const disclaimerKey = `cookieDisclaimer_${disclaimerDate}`;

const emptySubscribe = () => () => {};
const useHasMounted = () =>
  useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );

const readStoredAcceptance = () => {
  try {
    return (
      typeof navigator !== 'undefined' &&
      navigator.cookieEnabled &&
      typeof window !== 'undefined' &&
      typeof window.localStorage !== 'undefined' &&
      !!window.localStorage.getItem(disclaimerKey)
    );
  } catch {
    return false;
  }
};

/**
 * CookieDisclaimer Component
 *
 * Displays a fixed banner at the bottom of the page informing users about cookie usage.
 * The banner appears until the user clicks "Okay", at which point their acceptance
 * is stored in localStorage and the banner is hidden on subsequent visits.
 *
 * Features:
 * - Checks localStorage to determine if user has already accepted
 * - Shows warning snackbar if cookies/localStorage are disabled
 * - Stores acceptance in localStorage when user clicks "Okay"
 * - Fixed position at bottom of viewport
 *
 * @returns {JSX.Element | null} The cookie disclaimer banner or null if already accepted
 */
const CookieDisclaimer = () => {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const mounted = useHasMounted();
  const storedAccepted = useSyncExternalStore(
    emptySubscribe,
    readStoredAcceptance,
    // SSR: treat as accepted so the banner stays hidden until client hydration.
    () => true,
  );
  const [dismissed, setDismissed] = useState(false);

  /**
   * Warn once on the client when cookies/localStorage are unavailable.
   */
  useEffect(() => {
    if (!mounted) return;
    if (!navigator.cookieEnabled || typeof window.localStorage === 'undefined') {
      enqueueSnackbar('Site missing permissions', {
        variant: 'warning',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  }, [mounted, enqueueSnackbar]);

  /**
   * Handles the "Okay" button click.
   * Stores the user's acceptance in localStorage and hides the banner.
   */
  const setDisclaimerAsRead = useCallback(() => {
    try {
      window.localStorage.setItem(disclaimerKey, 'true');
    } catch {
      // Ignore storage failures; still dismiss for this session.
    }
    setDismissed(true);
  }, []);

  if (!mounted || storedAccepted || dismissed) {
    return null;
  }

  return (
    <Box className={classes.root} data-component="CookieDisclaimer">
      <Box className={classes.content}>
        <Typography variant="body2" className={classes.message}>
          {disclaimerText}
        </Typography>
        <Button
          onClick={setDisclaimerAsRead}
          variant="outlined"
          className={classes.acceptButton}
        >
          Okay
        </Button>
      </Box>
    </Box>
  );
};

export default CookieDisclaimer;
